import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useMutation } from "@tanstack/react-query";
import type { AxiosError } from "axios";

import { QUERY_KEYS } from "@app/dashboard/constants/query-keys";
import {
  CHECKOUT_CUSTOMIZATION_EVENTS_VALUES,
  trackFieldsUpdate
} from "@app/dashboard/constants/tracking_events/checkout-customization";
import {
  useCheckoutCustomizationStore
} from "@app/dashboard/store/checkout-customization";
import { useLangStore } from "@app/dashboard/store/lang";
import {
  updateCustomizationLogo
} from "@app/dashboard/services/inner_layout/checkout_customization/update-logo";
import { getServerUrl } from "@app/dashboard/utils/serverUrls";
import {
  ALLOWED_FORMATS,
  CUSTOMIZATION_SECTIONS_KEYS,
  MAX_LOGO_SIZE
} from "../../../../utils";
import { ImageUploader as ImageUploaderSection } from "@app/dashboard/components/shared/ImageUploader";
import styles from "./image-uploader.module.scss";


const { BUSINESS_LOGO } = CUSTOMIZATION_SECTIONS_KEYS;
const baseUrl = getServerUrl();

const RemotePaths = [
  '/epsilon/unified',
  '/elmuchacho/unified',
];

const handleImgPath = (path: string) => {
  const isLocalPath = !RemotePaths.some((prefix) =>
    path.includes(prefix),
  );

  return isLocalPath ? path : `${baseUrl}${path}`;
};


export const ImageUploader = () => {
  const { t } = useTranslation();
  const { lang } = useLangStore();

  const {
    errors,
    setErrors,
    setIsLoading,
    isFetched,
    businessLogo,
    setBusinessLogo
  } = useCheckoutCustomizationStore();

  const [previewImage, setPreviewImage] = useState<string | ArrayBuffer | null>();

  const {
    isPending,
    mutate,
  } = useMutation({
    mutationKey: [QUERY_KEYS.UPDATE_BUSINESS_LOGO],
    mutationFn: (imgFile: File) => updateCustomizationLogo({ merchantLogo: imgFile }),
    onSuccess: (response) => {
      const responseData = response?.data;
      const { merchant_logo } = responseData;

      setBusinessLogo({
        ...businessLogo,
        path: merchant_logo
      });

      trackFieldsUpdate(
        CHECKOUT_CUSTOMIZATION_EVENTS_VALUES.TABS.BRANDING,
        CHECKOUT_CUSTOMIZATION_EVENTS_VALUES.SERVER_SIDE_FIELDS_NAMES.MERCHANT_LOGO
      );
    },
    onError: (err) => {
      const axiosErr = err as AxiosError;
      const response = axiosErr?.response as any;
      const msg = response?.data?.message;
      const fieldMsg = response?.data?.[BUSINESS_LOGO]?.[0];

      setErrors(BUSINESS_LOGO, fieldMsg || msg || t('innerLayout.errors.smthWentWrong'));
    },
    onSettled: () => {
      setIsLoading(false);
    }
  });

  useEffect(() => {
    isFetched && setPreviewImage(businessLogo.path);
  }, [businessLogo.path, isFetched]);


  const onClickHandler = () => {
    setErrors(BUSINESS_LOGO, '');
  };

  const onLogoSelectHandler = ({ file }: { file?: File }) => {
    if (file) {
      const READER = new FileReader();
      READER.readAsDataURL(file);

      READER.onloadend = () => {
        if (READER.readyState === 2) {
          setIsLoading(true);

          setPreviewImage(READER.result);
          mutate(file);

          setBusinessLogo({
            path: READER.result as string,
            file: file || null
          });
        }
      };
    }
  };

  const onDeleteHandler = () => {
    setPreviewImage('');
    setErrors(BUSINESS_LOGO, '');
    setBusinessLogo({
      path: null,
      file: null
    });

    trackFieldsUpdate(
      CHECKOUT_CUSTOMIZATION_EVENTS_VALUES.TABS.BRANDING,
      CHECKOUT_CUSTOMIZATION_EVENTS_VALUES.SERVER_SIDE_FIELDS_NAMES.MERCHANT_LOGO
    );
  };


  return (
    <div className={styles["image-uploader"]}>
      <ImageUploaderSection
        lang={lang}
        size="sm"
        label={t('innerLayout.checkoutCustomizationPage.tabs.branding.sections.businessLogo.label')}
        hint={t('innerLayout.checkoutCustomizationPage.tabs.branding.sections.businessLogo.hint')}
        showReplaceBtn
        error={errors[BUSINESS_LOGO]}
        imgUrl={previewImage ? handleImgPath(previewImage as string) : ''}
        imgName={t('innerLayout.checkoutCustomizationPage.tabs.branding.sections.businessLogo.label')}
        disabled={isPending}
        allowedFormats={ALLOWED_FORMATS}
        allowedSize={MAX_LOGO_SIZE}
        onFileSelect={onLogoSelectHandler}
        onDeleteHandler={onDeleteHandler}
        onClickHandler={onClickHandler}
      />
    </div>
  )
}
